import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { UnifiedFlowService } from '../unified.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { RightBridgeApiService } from '../../services/right-bridge-api.service';
import { Global } from '../../models/global.model';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { NgxLoadingModule } from 'ngx-loading';
import { MarkdownModule } from 'ngx-markdown';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SpeechToTextComponent } from '../../shared-components/speech-to-text/speech-to-text.component';

export interface NoteData {
  profile: string;
  bridgeId: string;
  note: string;
  prompt: string;
  app: string;
  required: boolean;
}

@Component({
  selector: 'app-notes-entry',
  templateUrl: './notes-entry.component.html',
  styleUrls: ['./notes-entry.component.scss'],
  imports: [
    MatFormFieldModule,
    CommonModule,
    MatIconModule,
    MatInputModule,
    MatButtonModule,
    NgxLoadingModule,
    MarkdownModule,
    MatCheckboxModule,
    SpeechToTextComponent,
    ReactiveFormsModule,
  ],
})
export class NotesEntryComponent implements OnInit, OnChanges {
  private unfFlowSvc = inject(UnifiedFlowService);
  private ss = inject(SessionStorageService);
  private rbs = inject(RightBridgeApiService);
  private snacky = inject(MatSnackBar);

  @Input() notesData: NoteData = {} as NoteData;
  @Input() bridgeVal;
  @Input() currentBridge;
  @Input() moduleNotes = false;
  @Input() moduleBridges;
  @Input() externalLoading = false;
  @Output() noteEntered = new EventEmitter();
  @Output() setExternalLoad = new EventEmitter();
  @Output() updateModuleNote = new EventEmitter();
  @ViewChild('noteInput', { static: false })
  noteInput: ElementRef<HTMLInputElement>;

  canSpeechToText = false;
  containsPii = false;
  fieldTouched = false;
  globals: Global;
  loading = false;
  maxSubmissionCount = 20;
  moduleNoteSaved = false;
  note;
  noteInputControl = new FormControl('', [
    Validators.minLength(10),
    Validators.pattern('((?!\\s{4})[\\s\\S])*'),
  ]);
  noteEntry = new Subject<string>();
  noteSaved = false;
  noteScore = '';
  noteSpeechError: string;
  rights: string[];
  scoreMessage = '';
  submissionCount = 0;
  typedText: string;
  noteShouldProcessByAi = false;
  noteProcessedByAi = false;
  speechReviewed: boolean = true;
  voiceReviewed: boolean = false;
  usedVoice: boolean = false;
  displayFinalSubmissionNote = false;
  finalResponseMessage: string;

  ngOnInit(): void {
    this.readInputData();
  }

  ngOnChanges() {
    this.readInputData();
  }

  readInputData() {
    this.globals = this.ss.get('globals');
    this.rights = this.ss.get('rights');
    this.canSpeechToText = this.rights?.includes('SpeechToText');
    this.maxSubmissionCount =
      this.globals?.GlobalsGroup?.UnitAISettings?.MaxSubmissions || 1;
    this.note = this.notesData.note;
    this.noteSaved = false;
    this.noteProcessedByAi = false;
    this.containsPii = false;
    this.noteSpeechError = '';
    this.typedText = this.note;
    this.finalResponseMessage =
      this.globals?.GlobalsGroup?.UnitAISettings?.FinalResponseMessage;

    if (this.notesData.required)
      this.noteInputControl.addValidators(Validators.required);
    this.noteInputControl.markAllAsTouched();

    if ((this.note && this.moduleNotes) || !this.notesData.required) {
      this.moduleNoteSaved = true;
    }
    this.noteEntry
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(() => {
        this.fieldTouched = true;
      });

    if (
      this.rights.includes('ReadOnly') ||
      this.rights.includes('ReadOnlyAccess')
    ) {
      this.noteInputControl.disable();
    }

    if (
      this.rights.includes('NoteValidation') &&
      (this.rights.includes('ShowNoteAssistant') ||
        (this.rights.includes('NoteAssistantOnRequired') &&
          this.notesData.required))
    ) {
      this.noteShouldProcessByAi = true;
    }
  }

  buildNoteContextData(bridgeData: Record<string, any> = {}): Record<string, any> {
    const app = this.notesData.app;

    // No app should select a bridge here based on SelectedBridge or
    // assessmenttype — the caller already supplies the single correct
    // bridge/product context (currentBridge for a per-bridge note, or the
    // relevant bridge picked from moduleBridges for a module note).
    bridgeData = bridgeData ?? {};

    let datapoint1 = null;
    let datapoint2 = null;

    switch (app) {
      case 'pp':
        datapoint1 = bridgeData.BridgeType ?? null;
        datapoint2 = bridgeData.name ?? null;
        break;
      case 'aw':
        datapoint1 = bridgeData.ProductType ?? null;
        datapoint2 = bridgeData.assessmenttype ?? null;
        break;
      case 'iw':
        datapoint1 = bridgeData.BridgeNoteType ?? null;
        break;
      case 'li':
        datapoint1 = bridgeData.BridgeType ?? null;
        break;
    }

    return {
      Datapoint1: datapoint1,
      Datapoint2: datapoint2,
      ScoreClass: bridgeData.ScoreClass ?? null,
      SubmissionCount: this.submissionCount ?? null,
      MaxSubmissions: this.maxSubmissionCount ?? null,
    };
  }

  noteTyped(ev) {
    this.typedText = ev.target.value;
    this.noteEntry.next(ev);
  }

  saveNote(noteScoreAi?) {
    this.noteSaved = false;
    this.containsPii = this.checkForPii(this.note);

    if (this.externalLoading) {
      this.setExternalLoad.emit(true);
    } else {
      this.loading = true;
    }

    if (!this.containsPii) {
      const valid = this.notesData.required ? this.note.length > 0 : true;
      let saveNotePayload;
      this.noteEntered.emit(valid);

      if (this.notesData.app === 'pp') {
        saveNotePayload = { ClientProfiling: { Notes: this.note } };
      }

      if (this.notesData.app === 'aw') {
        saveNotePayload = { AnnuityWizard: { Note: this.note } };
      }

      if (this.notesData.app === 'iw') {
        saveNotePayload = { InvestmentWizard: { ValidationNote: this.note } };
      }

      if (this.notesData.app === 'li') {
        saveNotePayload = { LifeWizard: { Note: this.note } };
      }

      if (this.moduleNotes) {
        if (noteScoreAi) {
          saveNotePayload = {
            ...saveNotePayload,
            ClientProfilingOut: {
              ['AINoteStatus' + this.notesData.app.toUpperCase()]: noteScoreAi,
            },
          };
        }
        this.rbs
          .saveProfile(this.notesData.profile, saveNotePayload, false, false)
          .subscribe({
            next: () => {
              this.moduleNoteSaved = true;
              this.noteSaved = true;
              if (noteScoreAi) {
                this.noteProcessedByAi = true;
              }
              this.fieldTouched = false;

              if (this.externalLoading) {
                this.setExternalLoad.emit(false);
              } else {
                this.loading = false;
              }
            },
            error: err => {
              console.error('ERROR: ', err);
              this.snacky.open(
                `There was an error saving this note.`,
                'Close',
                {
                  duration: 4000,
                }
              );

              if (this.externalLoading) {
                this.setExternalLoad.emit(false);
              } else {
                this.loading = false;
              }
            },
          });
      } else {
        this.unfFlowSvc
          .saveNote(
            this.notesData.profile,
            this.note,
            this.notesData.bridgeId,
            this.notesData.app
          )
          .subscribe({
            next: x => {
              if (x.result === 'Success') {
                if (noteScoreAi) {
                  saveNotePayload = {
                    ClientProfilingOut: {
                      ['AINoteStatus' + this.notesData.app.toUpperCase()]:
                        noteScoreAi,
                    },
                  };
                  this.rbs
                    .saveProfile(
                      this.notesData.profile,
                      saveNotePayload,
                      false,
                      false
                    )
                    .subscribe({
                      next: () => {
                        this.moduleNoteSaved = true;
                        this.noteSaved = true;
                        this.noteProcessedByAi = true;
                        this.fieldTouched = false;
                        if (this.externalLoading) {
                          this.setExternalLoad.emit(false);
                        } else {
                          this.loading = false;
                        }
                      },
                      error: err => {
                        console.error('ERROR: ', err);
                        this.snacky.open(
                          `There was an error saving this note.`,
                          'Close',
                          {
                            duration: 4000,
                          }
                        );
                        if (this.externalLoading) {
                          this.setExternalLoad.emit(false);
                        } else {
                          this.loading = false;
                        }
                      },
                    });
                } else {
                  this.noteSaved = true;
                  this.fieldTouched = false;
                  if (this.externalLoading) {
                    this.setExternalLoad.emit(false);
                  } else {
                    this.loading = false;
                  }
                }
              } else {
                this.snacky.open(
                  `There was an error saving this note.`,
                  'Close',
                  {
                    duration: 4000,
                  }
                );
                if (this.externalLoading) {
                  this.setExternalLoad.emit(false);
                } else {
                  this.loading = false;
                }
              }
            },
            error: err => {
              console.error('ERROR: ', err);
              this.snacky.open(
                `There was an error saving this note.`,
                'Close',
                {
                  duration: 4000,
                }
              );
              if (this.externalLoading) {
                this.setExternalLoad.emit(false);
              } else {
                this.loading = false;
              }
            },
          });
      }
    } else {
      if (this.externalLoading) {
        this.setExternalLoad.emit(false);
      } else {
        this.loading = false;
      }
    }
  }

  validateAndSaveNote() {
    this.noteSaved = false;
    this.noteProcessedByAi = false;
    this.containsPii = this.checkForPii(this.note);

    if (this.externalLoading) {
      this.setExternalLoad.emit(true);
    } else {
      this.loading = true;
    }

    if (!this.containsPii) {
      if (this.moduleNotes) {
        if (this.submissionCount < this.maxSubmissionCount) {
          const moduleBridgeList = this.moduleBridges ?? this.bridgeVal ?? [];
          const noteContext = this.buildNoteContextData(
            (Array.isArray(moduleBridgeList)
              ? (moduleBridgeList.find(bridge =>
                  this.isEligibleModuleBridge(bridge)
                ) ?? moduleBridgeList[0])
              : moduleBridgeList) ?? {}
          );
          this.unfFlowSvc
            .validateNote(
              this.notesData.prompt,
              this.collectModuleBridges(),
              this.note,
              this.notesData.profile,
              this.notesData.app,
              this.submissionCount,
              this.maxSubmissionCount,
              noteContext
            )
            .subscribe({
              next: resp => {
                this.noteProcessedByAi = true;
                this.noteScore = resp['NoteScore'];
                this.submissionCount = this.submissionCount + 1;
                this.scoreMessage = resp['NoteUserResponse'];

                let aiNoteScore;

                switch (this.noteScore) {
                  case 'positive':
                    aiNoteScore = 'Pass';
                    break;
                  case 'negative':
                    aiNoteScore = 'Fail';
                    break;

                  default:
                    break;
                }

                this.saveNote(aiNoteScore);

                this.sendModuleNote();
              },
              error: err => {
                console.error('ERROR: ', err);
                this.noteScore = 'error';
                this.scoreMessage =
                  'An unexpected error occured while reviewing this note.';
                this.saveNote();
              },
            });
        } else if (this.submissionCount >= this.maxSubmissionCount) {
          this.noteScore = 'positive';
          this.saveNote();
          this.sendModuleNote();
          this.displayFinalSubmissionNote =
            this.finalResponseMessage.length > 0;
        }
      } else {
        const noteContext = this.buildNoteContextData(this.currentBridge ?? {});
        const bridgeData = this.cleanReasonText(this.bridgeVal);
        if (this.submissionCount < this.maxSubmissionCount) {
          this.unfFlowSvc
            .validateNote(
              this.notesData.prompt,
              bridgeData,
              this.note,
              this.notesData.profile,
              this.notesData.app,
              this.submissionCount,
              this.maxSubmissionCount,
              noteContext
            )
            .subscribe({
              next: resp => {
                this.noteProcessedByAi = true;
                this.noteScore = resp['NoteScore'];
                this.submissionCount = this.submissionCount + 1;
                this.scoreMessage = resp['NoteUserResponse'];

                let aiNoteScore;

                switch (this.noteScore) {
                  case 'positive':
                    aiNoteScore = 'Pass';
                    break;
                  case 'negative':
                    aiNoteScore = 'Fail';
                    break;

                  default:
                    break;
                }

                this.saveNote(aiNoteScore);
              },
              error: err => {
                console.error('ERROR: ', err);
                this.noteScore = 'error';
                this.scoreMessage =
                  'An unexpected error occured while reviewing this note.';
                this.saveNote();
              },
            });
        } else if (this.submissionCount >= this.maxSubmissionCount) {
          this.noteScore = 'positive';
          this.saveNote();
          this.displayFinalSubmissionNote =
            this.finalResponseMessage.length > 0;
        }
      }
    } else {
      if (this.externalLoading) {
        this.setExternalLoad.emit(false);
      } else {
        this.loading = false;
      }
    }
  }

  checkForPii(text) {
    let containsPii = false;

    const regexObject = {
      creditCard: new RegExp(
        /\b(?:\d{4}[- ]?){3}\d{4}\b|\b\d{4}[- ]?\d{6}[- ]?\d{5}\b/gim
      ),
      ssn: new RegExp(/\b(?:\d{3}-\d{2}-\d{4}|\d{3} \d{2} \d{4}|\d{9})\b/gim),
      acctNums: new RegExp(
        // eslint-disable-next-line no-useless-escape
        /(?<![\$.])\b(?:[A-Za-z]{2}\d{7}|\d{7}|\d{6}|\d{8})\b(?!%)/gim
      ),
    };

    Object.keys(regexObject).forEach(key => {
      if (regexObject[key].test(text)) {
        containsPii = true;
      }
    });

    return containsPii;
  }

  sendModuleNote() {
    this.updateModuleNote.emit(this.note);
  }

  collectModuleBridges() {
    const bridgePayload = [];

    this.moduleBridges.map(bridge => {
      if (this.isEligibleModuleBridge(bridge)) {
        bridge.trueRules.map(trueRule => {
          if (trueRule.reasonType === 'Negative') {
            bridgePayload.push(trueRule);
          }
        });
      }
    });

    return bridgePayload;
  }

  isEligibleModuleBridge(bridge): boolean {
    return (
      (this.notesData.app === 'pp' &&
        (bridge.BridgeType === 'rollover' ||
          (bridge.BridgeType === 'product' &&
            bridge.assessmenttype === 'validate'))) ||
      (this.notesData.app === 'aw' &&
        (bridge.SelectedBridge === 'Yes' ||
          bridge.assessmenttype === 'exchange')) ||
      (this.notesData.app === 'iw' &&
        (bridge.SelectedBridge === 'Yes' ||
          bridge.BridgeType === 'program' ||
          bridge.BridgeType === 'portfolio')) ||
      (this.notesData.app === 'li' && bridge.SelectedBridge === true)
    );
  }

  closeValidation() {
    this.noteScore = '';
    this.scoreMessage = '';
  }

  closePii() {
    this.containsPii = false;
  }

  cleanReasonText(reasons) {
    const resp = [];

    reasons.forEach(el => {
      const reason = el;
      delete reason.fired;
      delete reason.name;
      delete reason.reason;
      delete reason.score;

      reason.explanation = this.cleanHtml(reason.explanation);
      reason.headline = this.cleanHtml(reason.headline);

      resp.push(reason);
    });

    const cleanedList = resp.filter(item => item.reasonType === 'Negative');

    return cleanedList;
  }

  cleanHtml(value) {
    const htmlRegEx = /<(?:"[^"]*"['"]*|'[^']*'['"]*|[^'">])+>/g;
    return value.replace(htmlRegEx, '');
  }

  applySpeechToTextNote(ev) {
    this.note = ev;
    this.typedText = this.note;
    this.noteEntry.next(ev);
    this.noteInput.nativeElement.focus();
    this.voiceReviewed = false;
    this.usedVoice = true;
  }

  applySpeechToTextError(ev) {
    if (ev === 'reset') {
      this.noteSpeechError = null;
    } else {
      this.noteSpeechError = ev;
    }
  }

  speechReviewEvent(ev) {
    this.voiceReviewed = ev.checked;
  }
}
